import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, LucideIcon } from 'lucide-react';

interface ActionButton {
  text: string;
  href: string;
  type: 'primary' | 'secondary' | 'tertiary';
  target?: '_blank' | '_self';
  title?: string;
  ariaLabel?: string;
}

interface HeroProfessionalProps {
  title: string;
  subtitle: string;
  keyPoints: {
    icon: LucideIcon;
    text: string;
  }[];
  backgroundImage: string;
  imageAlt: string;
  actionButtons?: ActionButton[];
}

const HeroProfessional = ({
  title,
  subtitle,
  keyPoints,
  backgroundImage,
  imageAlt,
  actionButtons = []
}: HeroProfessionalProps) => {
  return (
    <section className="bg-primary text-primary-foreground py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center mb-6">
          <Button asChild variant="ghost" className="text-primary-foreground hover:bg-primary-foreground/10 mr-4">
            <Link to="/">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </Link>
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              {title}
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 leading-relaxed">
              {subtitle}
            </p>
            
            {/* Points clés avec icônes */}
            <div className="space-y-4">
              {keyPoints.map((point, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center mr-4">
                    <point.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="text-lg">{point.text}</span>
                </div>
              ))}
            </div>
            
            {/* Boutons d'action */}
            {actionButtons.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                {actionButtons.map((button, index) => {
                  const buttonClasses = {
                    primary: "bg-white text-primary hover:bg-white/90 font-semibold px-6 py-3 rounded-md transition-all duration-300 hover:scale-105 shadow-lg",
                    secondary: "bg-transparent text-white border-2 border-white hover:bg-white/10 font-semibold px-6 py-3 rounded-md transition-all duration-300 hover:scale-105",
                    tertiary: "bg-white/10 text-white hover:bg-white/20 font-normal px-4 py-2 rounded border border-white/30 transition-all duration-300 hover:scale-95 text-sm"
                  };
                  
                  return (
                    <Button
                      key={index}
                      asChild
                      className={buttonClasses[button.type]}
                    >
                      <Link
                        to={button.href}
                        target={button.target || '_self'}
                        title={button.title || button.text}
                        aria-label={button.ariaLabel || button.text}
                      >
                        {button.text}
                      </Link>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="relative">
            <div 
              className="h-96 bg-cover bg-center bg-no-repeat rounded-2xl"
              style={{
                backgroundImage: `url('${backgroundImage}')`,
              }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroProfessional;